import type { Dispatch, SetStateAction } from "react";
import React from "react";
import type { GroupBase, OptionProps } from "react-select";
import { components } from "react-select";
import type { Props } from "react-select";

import { classNames } from "@calcom/lib";
import { useLocale } from "@calcom/lib/hooks/useLocale";

import { Select } from "../select";

export type Option = {
  value: any;
  label: string;
};

const InputOption: React.FC<OptionProps<any, boolean, GroupBase<any>>> = ({
  isDisabled,
  isFocused,
  isSelected,
  children,
  innerProps,
  className,
  ...rest
}) => {
  const props = {
    ...innerProps,
  };

  return (
    <components.Option
      className={classNames(
        className,
        "my-1 !flex !cursor-pointer !py-3 text-[inherit]",
        isFocused && "dark:!bg-darkgray-200 !rounded-md !bg-gray-100",
        isSelected && "!bg-booking-new rounded-lg !text-black"
      )}
      {...rest}
      isDisabled={isDisabled}
      isFocused={isFocused}
      isSelected={isSelected}
      innerProps={props}>
      <input
        type="checkbox"
        className="focus:ring-primary-500 h-4 w-4 rounded border-[#2664EC] text-[#3B82F6] ltr:mr-2 rtl:ml-2"
        checked={isSelected}
        readOnly
      />
      {children}
    </components.Option>
  );
};

type MultiSelectionCheckboxesProps = {
  options: { value: any; label: string }[];
  setSelected: Dispatch<SetStateAction<Option[]>>;
  selected: Option[];
  placeholder: string;
  hasIcon?: boolean;
};

const MultiValue = ({ index, getValue }: { index: number; getValue: any }) => {
  const { t } = useLocale();

  const value = getValue();

  return (
    <>
      {!index && (
        <div className="flex">
          <div className={value.length > 1 ? "max-w-52" : "max-w-64"}>
            <span className="line-clamp-1">{t(value[0].label)}</span>
          </div>
          {value.length > 1 ? `, ${value.length - 1} More` : null}
        </div>
      )}
    </>
  );
};

export default function MultiSelectCheckboxes({
  options,
  isLoading,
  selected,
  setSelected,
  className,
  placeholder,
  instanceId,
  hasIcon = false,
  ...props
}: Omit<Props, "options"> & MultiSelectionCheckboxesProps) {
  const additionalComponents = { MultiValue };

  return (
    <Select
      instanceId={instanceId}
      value={selected}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onChange={(s: any) => {
        setSelected(s);
      }}
      styles={{
        control: (baseStyles) => ({
          ...baseStyles,
          borderColor: "rgb(229 231 235)",
          paddingLeft: hasIcon ? "28px" : "0px",
        }),
        valueContainer: (baseStyles) => ({
          ...baseStyles,
          paddingLeft: "0px",
        }),
      }}
      options={options}
      isMulti
      className={classNames(className ? className : "w-64 text-sm")}
      isSearchable={true}
      closeMenuOnSelect={false}
      placeholder={placeholder}
      hideSelectedOptions={false}
      isLoading={isLoading}
      components={{
        ...additionalComponents,
        Option: InputOption,
      }}
      {...props}
    />
  );
}
